import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Menu, MenuVariant, MenuSize } from '../Menu';
import type { DropdownProps, DropdownPlacement } from './types';

const defaultPlacement: DropdownPlacement = 'bottom-end';

const Dropdown: React.FC<DropdownProps> = ({
  trigger,
  items,
  onItemClick,
  selectedId,
  open: controlledOpen,
  onOpenChange,
  placement = defaultPlacement,
  menuWidth = '232px',
  menuFullWidth = false,
  menuVariant = MenuVariant.Light,
  menuSize = MenuSize.Medium,
  showLeadingIcons = true,
  showTrailingArrows = false,
  disabled = false,
  readOnly = false,
  error = false,
  errorMessage,
  supportingText,
  showSearch = false,
  searchPlaceholder,
  showCheckboxes = false,
  multiSelect = false,
  className = '',
  id,
  toolTip,
  onScrollEnd,
  onSearchChange,
  menuMaxVisibleItems,
  autoMaxHeight = false,
}) => {
  const hasError = error || !!errorMessage;
  const isControlled =
    controlledOpen !== undefined && onOpenChange !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const [computedMaxHeight, setComputedMaxHeight] = useState<
    number | undefined
  >(undefined);
  const [effectivePlacement, setEffectivePlacement] =
    useState<DropdownPlacement>(placement);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const open = isControlled ? controlledOpen : internalOpen;

  const setOpen = useCallback(
    (value: boolean) => {
      if (!isControlled) {
        setInternalOpen(value);
      }
      onOpenChange?.(value);
    },
    [isControlled, onOpenChange]
  );

  const toggle = useCallback(() => {
    if (readOnly) {
      return;
    }
    setOpen(!open);
  }, [readOnly, setOpen, open]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, isControlled, setOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, setOpen]);

  useEffect(() => {
    if (!open) {
      setEffectivePlacement(placement);
      return;
    }
    if (!autoMaxHeight) {
      return;
    }
    const MIN_MENU_HEIGHT = 120;
    const compute = () => {
      if (!dropdownRef.current) {
        return;
      }
      const triggerRect = dropdownRef.current.getBoundingClientRect();
      let bottomBoundary = window.innerHeight - 16;

      // Walk up to the nearest non-static positioned ancestor (e.g. a filter popup),
      // then look for a [data-dropdown-boundary] marker inside it.
      let ancestor: HTMLElement | null = dropdownRef.current.parentElement;
      while (ancestor && ancestor !== document.body) {
        if (window.getComputedStyle(ancestor).position !== 'static') {
          const marker = ancestor.querySelector('[data-dropdown-boundary]');
          if (marker) {
            bottomBoundary = marker.getBoundingClientRect().top - 8;
          }
          break;
        }
        ancestor = ancestor.parentElement;
      }

      const spaceBelow = bottomBoundary - triggerRect.bottom;
      // Flip to top when there isn't enough room below.
      if (spaceBelow < MIN_MENU_HEIGHT) {
        const spaceAbove = triggerRect.top - 16;
        const flipped = placement.replace('bottom', 'top') as DropdownPlacement;
        setEffectivePlacement(flipped);
        setComputedMaxHeight(Math.max(40, spaceAbove));
      } else {
        setEffectivePlacement(placement);
        setComputedMaxHeight(Math.max(40, spaceBelow));
      }
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [open, autoMaxHeight, placement]);

  const handleMenuClick = (itemId: string) => {
    const item = items.find((i) => i.id === itemId);
    if (item?.onClick) {
      item.onClick(itemId);
    }
    onItemClick?.(itemId);
    if (!multiSelect) {
      setOpen(false);
    }
  };

  const menuPositionClasses: Record<DropdownPlacement, string> = {
    'bottom-end': 'right-0 left-auto top-full',
    'bottom-start': 'left-0 right-auto top-full',
    'top-end': 'right-0 left-auto bottom-full',
    'top-start': 'left-0 right-auto bottom-full',
  };

  const positionClass = menuPositionClasses[effectivePlacement];
  const resolvedMenuWidth = menuFullWidth
    ? '100%'
    : typeof menuWidth === 'number'
      ? `${menuWidth}px`
      : menuWidth;

  const [isFocused, setIsFocused] = useState(false);

  // TextField-style container (same look as TextField input area)
  const triggerContainerStyle: React.CSSProperties = {
    minHeight: 56,
    paddingLeft: 12,
    paddingRight: 12,
    borderBottom: hasError
      ? '2px solid #e11b1b'
      : open || isFocused
        ? '2px solid #2a4455'
        : 'none',
  };

  return (
    <div
      className={`relative w-full min-h-[56px] ${className}`}
      ref={dropdownRef}
      id={id}
      style={{ fontFamily: 'Roboto, sans-serif' }}
    >
      <div
        className={`
          relative flex items-center rounded-lg transition-all duration-150
          ${readOnly ? 'cursor-default bg-[#F6F6F6]' : disabled ? 'cursor-not-allowed bg-[#F6F6F6]' : 'bg-[#E5E7E9] hover:bg-neutral-85'}
        `}
        style={triggerContainerStyle}
        onFocus={() => !disabled && !readOnly && setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
      >
        {/* Unfocused bottom border (TextField-style) */}
        {!open && !isFocused && !hasError && (
          <div
            className="absolute bottom-0"
            style={{
              left: 6,
              right: 6,
              height: 1,
              backgroundColor: '#6a7278',
            }}
          />
        )}

        <div className="flex-1 min-w-0 flex items-center w-full pt-1 pb-1">
          {trigger({
            open,
            toggle,
            disabled,
            readOnly,
            error: hasError,
            singleOption: items.length === 1,
            toolTip,
          })}
        </div>
      </div>

      {/* Supporting Text / Error Message (TextField-style) — hidden while open so the menu positions flush against the trigger */}
      {!open && (supportingText || errorMessage) && (
        <p
          className={`
            mt-1 px-4 text-xs leading-4
            ${hasError ? 'text-error-40' : disabled && !readOnly ? 'text-neutral-60' : 'text-neutral-50'}
          `}
          style={{ fontSize: 12, lineHeight: '16px' }}
        >
          {errorMessage || supportingText}
        </p>
      )}

      {open && !readOnly && (
        <div
          className={`absolute z-50 ${positionClass} ${menuFullWidth ? 'left-0 right-0 w-full' : ''}`}
        >
          <Menu
            items={items}
            variant={menuVariant}
            size={menuSize}
            width={resolvedMenuWidth}
            showLeadingIcons={showLeadingIcons}
            showTrailingArrows={showTrailingArrows}
            onItemClick={handleMenuClick}
            showSearch={showSearch}
            searchPlaceholder={searchPlaceholder}
            showCheckboxes={showCheckboxes}
            multiSelect={multiSelect}
            toolTip={toolTip}
            onScrollEnd={onScrollEnd}
            {...(onSearchChange ? { onSearchChange } : {})}
            {...(selectedId != null ? { selectedId } : {})}
            {...(menuMaxVisibleItems !== undefined
              ? { maxVisibleItems: menuMaxVisibleItems }
              : {})}
            {...(autoMaxHeight && computedMaxHeight !== undefined
              ? { maxHeight: computedMaxHeight }
              : {})}
          />
        </div>
      )}
    </div>
  );
};

export default Dropdown;
